import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input } from '../atoms';
import { DataTable, useToast } from '../organisms';
import { useEntityPermission } from '../lib/usePermissions';
import type { ExemptionProposal, ProposalStatus, ActionType } from './ExemptionProposals';
import { ACTION_LABEL, money } from './ExemptionProposals';
import { formatDate } from '../lib/orgTimezone';
import { humanizeToken } from '../lib/inlineAttribution';

/**
 * ExemptionProposalDetail — /v2/parcels/exemption-proposals/:id ("Government mass update")
 *
 * The PREVIEW + OVERRIDE + SIGN-OFF surface of the government mass-update flow
 * (backlog 10.6 + Q6+Q7), wired to the REAL backend (src/routes/exemption-proposals.js):
 *   • preview  GET  /api/exemption-proposals/:id/preview  (computed impact, read-only)
 *   • edit     PUT  /api/exemption-proposals/:id          (per-parcel override / target)
 *   • apply    POST /api/exemption-proposals/:id/apply    (status-CAS proposed→applied)
 *   • cancel   POST /api/exemption-proposals/:id/cancel
 *
 * The preview shows, per in-scope parcel, the current → projected impact for the
 * proposal's action (exemption flags | cap | reduction). For a cap/reduction the
 * operator can EDIT a per-parcel override before sign-off. Dollar figures are redacted
 * server-side for a money-blind role (they arrive under denylisted keys and are stripped
 * at the res.json boundary), so this view renders "—" when a money field is absent.
 */

interface MoneyPair {
  exemption_amount?: number;
  tax_amount?: number;
  circuit_breaker_abatement?: number;
  reduction_current_amount?: number;
}
interface Flags { homestead: boolean; over_65: boolean }
interface PreviewLine {
  parcel_id: string;
  // exemption
  current_flags?: Flags;
  projected_flags?: Flags;
  gate_violation?: string[] | null;
  // cap
  current_circuit_breaker?: boolean;
  // reduction
  has_primary?: boolean;
  // shared
  override_applied?: boolean;
  current: MoneyPair;
  projected: MoneyPair;
  delta?: MoneyPair; // stripped for money-blind role
}
interface PreviewResponse extends ExemptionProposal {
  in_scope_count: number;
  lines: PreviewLine[];
  totals: { current: MoneyPair; projected: MoneyPair; delta?: MoneyPair };
}

const STATUS_VARIANT: Record<ProposalStatus, 'success' | 'warning' | 'neutral'> = {
  proposed: 'warning', applied: 'success', cancelled: 'neutral',
};

function flagChips(f: Flags) {
  const on = [f.homestead && 'homestead', f.over_65 && 'over-65'].filter(Boolean) as string[];
  return on.length ? on.join(', ') : 'none';
}
/** A colored signed-money cell (green = tax down, red = tax up). */
function DeltaTax({ d }: { d: number | undefined }) {
  if (d == null) return <>—</>;
  const color = d < 0 ? 'var(--color-success)' : d > 0 ? 'var(--color-danger)' : 'var(--color-ink-3)';
  return <span style={{ color, fontWeight: 'var(--fw-medium)' }}>{d > 0 ? '+' : ''}{money(d)}</span>;
}

export function ExemptionProposalDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const canEdit = useEntityPermission('parcel', 'can_edit');

  const previewQuery = useQuery<PreviewResponse>({
    queryKey: ['exemption-proposal-preview', id],
    queryFn: ({ signal }) => api.get<PreviewResponse>(`/api/exemption-proposals/${id}/preview`, signal),
    enabled: !!id,
  });

  // Per-parcel override draft (cap/reduction), seeded once from the loaded proposal.
  const [overrideDraft, setOverrideDraft] = useState<Record<string, string>>({});
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (previewQuery.data && !seeded) {
      const seed: Record<string, string> = {};
      for (const [pid, o] of Object.entries(previewQuery.data.overrides || {})) {
        if (o && o.amount != null) seed[pid] = String(o.amount);
      }
      setOverrideDraft(seed);
      setSeeded(true);
    }
  }, [previewQuery.data, seeded]);

  const onError = (err: Error) => {
    const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
      ? String((err.body as { error: unknown }).error) : err.message;
    toast({ variant: 'error', message: msg });
  };
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['exemption-proposal-preview', id] });
    queryClient.invalidateQueries({ queryKey: ['exemption-proposals'] });
    queryClient.invalidateQueries({ queryKey: ['parcels'] });
  };

  const saveOverrides = useMutation({
    mutationFn: () => {
      const overrides: Record<string, { amount: number }> = {};
      for (const [pid, val] of Object.entries(overrideDraft)) {
        const n = Number(val);
        if (val.trim() !== '' && Number.isFinite(n) && n >= 0) overrides[pid] = { amount: n };
      }
      return api.put(`/api/exemption-proposals/${id}`, { overrides });
    },
    onSuccess: () => { toast({ variant: 'success', message: 'Overrides saved.' }); invalidate(); },
    onError,
  });

  const apply = useMutation({
    mutationFn: () => api.post(`/api/exemption-proposals/${id}/apply`, {}),
    onSuccess: (res: unknown) => {
      const r = res as { applied?: number; skipped?: unknown[] };
      toast({ variant: 'success', message: `Applied to ${r.applied ?? 0} parcel(s)${r.skipped && r.skipped.length ? `, ${r.skipped.length} skipped` : ''}.` });
      invalidate();
    },
    onError,
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/api/exemption-proposals/${id}/cancel`, {}),
    onSuccess: () => { toast({ variant: 'success', message: 'Proposal cancelled.' }); invalidate(); },
    onError,
  });

  const p = previewQuery.data;
  const action: ActionType = p?.action_type ?? 'exemption';
  const isProposed = p?.status === 'proposed';
  const overrideEditable = !!(canEdit && isProposed && action !== 'exemption');

  const parcelCol: ColumnDef<PreviewLine> = {
    accessorKey: 'parcel_id',
    header: 'Parcel',
    cell: ({ getValue }) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{String(getValue()).slice(0, 8)}</span>
    ),
  };
  const overrideCol: ColumnDef<PreviewLine> = {
    id: 'override',
    header: 'Override ($)',
    cell: ({ row }) => {
      const pid = row.original.parcel_id;
      if (!overrideEditable) {
        return row.original.override_applied
          ? <Badge variant="info">override</Badge>
          : <span style={{ color: 'var(--color-ink-3)' }}>—</span>;
      }
      return (
        <Input
          type="number"
          min="0"
          aria-label={`Override for ${pid}`}
          placeholder="target"
          value={overrideDraft[pid] ?? ''}
          onChange={(e) => setOverrideDraft((d) => ({ ...d, [pid]: e.target.value }))}
          style={{ maxWidth: 120 }}
        />
      );
    },
  };

  const columns = useMemo<ColumnDef<PreviewLine>[]>(() => {
    if (action === 'cap') {
      return [
        parcelCol,
        ...(action === 'cap' ? [overrideCol] : []),
        { id: 'abate', header: 'Abatement (proj.)', cell: ({ row }) => money(row.original.projected.circuit_breaker_abatement) },
        { id: 'cur_tax', header: 'Tax (now)', cell: ({ row }) => money(row.original.current.tax_amount) },
        { id: 'proj_tax', header: 'Tax (proj.)', cell: ({ row }) => money(row.original.projected.tax_amount) },
        { id: 'delta', header: 'Δ Tax', cell: ({ row }) => <DeltaTax d={row.original.delta?.tax_amount} /> },
      ];
    }
    if (action === 'reduction') {
      return [
        parcelCol,
        overrideCol,
        { id: 'cur_red', header: 'Reduction (now)', cell: ({ row }) => money(row.original.current.reduction_current_amount) },
        { id: 'proj_red', header: 'Reduction (proj.)', cell: ({ row }) => money(row.original.projected.reduction_current_amount) },
        { id: 'cur_tax', header: 'Tax', cell: ({ row }) => money(row.original.current.tax_amount) },
        {
          id: 'noprimary',
          header: '',
          cell: ({ row }) => row.original.has_primary === false ? <Badge variant="danger">no primary line: skipped</Badge> : null,
        },
      ];
    }
    // exemption (the original 10.6 columns)
    return [
      parcelCol,
      { id: 'from', header: 'Current flags', cell: ({ row }) => <span style={{ color: 'var(--color-ink-3)' }}>{flagChips(row.original.current_flags!)}</span> },
      { id: 'to', header: 'Projected flags', cell: ({ row }) => flagChips(row.original.projected_flags!) },
      { id: 'cur_ex', header: 'Exemption (now)', cell: ({ row }) => money(row.original.current.exemption_amount) },
      { id: 'proj_ex', header: 'Exemption (proj.)', cell: ({ row }) => money(row.original.projected.exemption_amount) },
      { id: 'cur_tax', header: 'Tax (now)', cell: ({ row }) => money(row.original.current.tax_amount) },
      { id: 'proj_tax', header: 'Tax (proj.)', cell: ({ row }) => money(row.original.projected.tax_amount) },
      { id: 'delta', header: 'Δ Tax', cell: ({ row }) => <DeltaTax d={row.original.delta?.tax_amount} /> },
      {
        id: 'flag',
        header: '',
        cell: ({ row }) => row.original.gate_violation ? <Badge variant="danger">gate: skipped</Badge> : null,
      },
    ];
  // overrideEditable + overrideDraft feed the override cell renderer.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, overrideEditable, overrideDraft]);

  if (previewQuery.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading preview…</p>;
  if (previewQuery.isError || !p) {
    return (
      <div>
        <Button variant="ghost" onClick={() => navigate('/parcels/exemption-proposals')}>← Back</Button>
        <p style={{ color: 'var(--color-danger)', marginTop: 'var(--sp-3)' }}>Proposal not found or failed to load.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <div>
        <Button variant="ghost" onClick={() => navigate('/parcels/exemption-proposals')}>← All proposals</Button>
      </div>

      <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 'var(--fs-3xl)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
            Government mass update
          </h1>
          <p style={{ margin: 'var(--sp-1) 0 0', color: 'var(--color-ink-3)' }}>
            <Badge variant="info">{ACTION_LABEL[action] ?? action}</Badge>
            {' · '}{p.in_scope_count.toLocaleString()} parcel{p.in_scope_count === 1 ? '' : 's'} in scope
            {p.note ? ` · ${p.note}` : ''}
          </p>
        </div>
        <Badge variant={STATUS_VARIANT[p.status]} dot>{humanizeToken(p.status)}</Badge>
      </header>

      {/* Totals summary */}
      <div style={{ display: 'flex', gap: 'var(--sp-4)', flexWrap: 'wrap' }}>
        {action === 'exemption' && <SummaryTile label="Exemption (projected)" value={money(p.totals.projected.exemption_amount)} />}
        {action !== 'exemption' && <SummaryTile label={action === 'cap' ? 'Cap target' : 'Reduction target'} value={money(p.target_amount)} />}
        <SummaryTile label="Tax (current)" value={money(p.totals.current.tax_amount)} />
        <SummaryTile label="Tax (projected)" value={money(p.totals.projected.tax_amount)} />
        <SummaryTile
          label="Net tax change"
          value={p.totals.delta?.tax_amount == null ? '—' : `${p.totals.delta.tax_amount > 0 ? '+' : ''}${money(p.totals.delta.tax_amount)}`}
        />
      </div>

      {/* Per-parcel impact */}
      <section>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)', margin: '0 0 var(--sp-2)' }}>
          <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', margin: 0 }}>Impact preview</h2>
          {overrideEditable && (
            <Button variant="secondary" size="sm" onClick={() => saveOverrides.mutate()} disabled={saveOverrides.isPending}>
              {saveOverrides.isPending ? 'Saving…' : 'Save overrides & re-preview'}
            </Button>
          )}
        </div>
        {p.lines.length === 0
          ? <p style={{ color: 'var(--color-ink-3)' }}>No in-scope parcels for your book.</p>
          : <DataTable data={p.lines} columns={columns} />}
        {overrideEditable && (
          <p style={{ margin: 'var(--sp-2) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            Leave an override blank to use the selection target. Save to re-preview the impact before signing off.
          </p>
        )}
      </section>

      {/* Sign-off */}
      {canEdit && isProposed && (
        <div style={{
          display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end',
          paddingTop: 'var(--sp-3)', borderTop: '1px solid var(--color-border)',
        }}>
          <Button variant="ghost" onClick={() => cancel.mutate()} disabled={cancel.isPending || apply.isPending}>
            {cancel.isPending ? 'Cancelling…' : 'Cancel proposal'}
          </Button>
          <Button variant="primary" onClick={() => apply.mutate()} disabled={apply.isPending || cancel.isPending}>
            {apply.isPending ? 'Applying…' : 'Sign off & apply'}
          </Button>
        </div>
      )}
      {p.status === 'applied' && (
        <p style={{ color: 'var(--color-success)', textAlign: 'right' }}>
          Applied{p.applied_at ? ` on ${formatDate(p.applied_at, { dateOnly: true })}` : ''}.
        </p>
      )}
    </div>
  );
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      flex: '1 1 160px', minWidth: 160, padding: 'var(--sp-3) var(--sp-4)',
      border: '1px solid var(--color-border)', borderRadius: 'var(--r-xl)', background: 'var(--color-card)',
    }}>
      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wide)' }}>{label}</div>
      <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 'var(--fw-semibold)', marginTop: 'var(--sp-1)' }}>{value}</div>
    </div>
  );
}
